import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Plus, Trash2, Link, Save, UserPlus, Edit2, Check, CircleMinus, CircleCheck, Key, Crown, Lock } from 'lucide-react';
import { EditUserModal, ResetPasswordModal, EditUserTarget, ResetPasswordTarget } from './UserEditModals';
import Modal from '../common/Modal';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import Toast, { ToastMessage } from '../common/Toast';
import { confirmDelete, confirmWarning, confirmActive } from '../common/confirm';
import {
    Agency, AgencyMember, AgencyMemberCreatePayload, AgencyLinkCandidate,
    AgencyUpdatePayload,
    ProgramTemplate,
    agencyService, agencyMemberService, programTemplateService,
} from '../../services/auditService';
import { userService } from '../../services/userService';
import { extractApiError } from '../../utils/apiError';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea } from '../../validation/ValidatedFields';
import { usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';
import { PlanPeriodicity, PlanYearType, timelineLabel } from '../../services/auditPlanService';
import AgencyPlanWindowFields, { PlanWindowValue } from './AgencyPlanWindowFields';
import auditUniverseService, { AgencyUniverseRow } from '../../services/auditUniverseService';

const AGENCY_LOCKED_MSG = 'Record is approved and locked. Reopen via the workflow button to edit.';

type Tab = 'edit' | 'users' | 'universes' | 'programs';

interface DropdownUser { id: number; name: string; email: string; }

interface AgencyConfigureModalProps {
    isOpen: boolean;
    onClose: () => void;
    agency: Agency | null;
    allPrograms: ProgramTemplate[];
    onChanged?: () => void;
    canEdit?: boolean;
    hideLinkUser?: boolean;
    onEdit?: (agency: Agency) => void;
    /** Tab to open on. Agency row-click passes 'users' so the agency's users
     *  (edit / reset password / remove) are shown immediately. Defaults to 'edit'. */
    initialTab?: Tab;
}

const AgencyConfigureModal: React.FC<AgencyConfigureModalProps> = ({
    isOpen, onClose, agency, allPrograms, onChanged, canEdit = true, hideLinkUser = false,
    initialTab = 'edit',
}) => {
    const [activeTab, setActiveTab] = useState<Tab>(initialTab);
    const { allowed: canModify, loading: permLoading } = usePermission('ADT_AGENCIES', 'view+modify');

    // Approved agencies are frozen: no field edits, no toggle, and the Team /
    // Programs action buttons stay visible but dimmed — clicking one shows a
    // lock toast instead of acting. Unlock via the status dot's Reopen action.
    const isApproved = (agency as any)?.workflow_state === 'approved';
    // Returns true (and shows the lock toast) when the record is approved, so a
    // handler can early-return. Lets disabled-looking buttons explain themselves.
    const blockedByLock = (): boolean => {
        if (isApproved) { setToast({ type: 'error', message: AGENCY_LOCKED_MSG }); return true; }
        return false;
    };

    // ── Basic Details tab state ──────────────────────────────────────────────
    const editFormRef = useRef<HTMLFormElement>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [agencyName, setAgencyName] = useState('');
    const [description, setDescription] = useState('');
    const [isGlobal, setIsGlobal] = useState(0);
    const [planWindow, setPlanWindow] = useState<PlanWindowValue>({ periodicity: 'QUARTERLY', yearType: 'FINANCIAL', startId: null, endId: null });
    // Universes tab — what the GRC team has mandated to this agency (AUA-10).
    const [universes, setUniverses] = useState<AgencyUniverseRow[] | null>(null);
    const [headUserId, setHeadUserId] = useState<number | ''>('');
    const [dropdownUsers, setDropdownUsers] = useState<DropdownUser[]>([]);
    const [dropdownLoading, setDropdownLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [toggling, setToggling] = useState(false);
    const editVh = useFieldValidation();

    // ── Users tab state ──────────────────────────────────────────────────────
    const [members, setMembers] = useState<AgencyMember[]>([]);
    // Kind-aware link candidates from the backend (External → External Auditors;
    // Internal → internal staff). Already excludes current members.
    const [candidates, setCandidates] = useState<AgencyLinkCandidate[]>([]);
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    // ── Programs tab state ───────────────────────────────────────────────────
    const [linkedPrograms, setLinkedPrograms] = useState<ProgramTemplate[]>([]);

    // ── Sub-modal states ─────────────────────────────────────────────────────
    const [showLinkUserModal, setShowLinkUserModal] = useState(false);
    const [linkUserId, setLinkUserId] = useState<number | ''>('');
    const [showLinkProgramModal, setShowLinkProgramModal] = useState(false);
    const [linkProgramId, setLinkProgramId] = useState<number | ''>('');
    const [showCreateUserModal, setShowCreateUserModal] = useState(false);
    const [newUser, setNewUser] = useState({ first_name: '', last_name: '', login_id: '', password: '' });
    const [creatingUser, setCreatingUser] = useState(false);
    const newUserVh = useFieldValidation();

    // ── Edit user / Reset password sub-modal states ──────────────────────────
    const [editUserTarget, setEditUserTarget] = useState<EditUserTarget | null>(null);
    const [resetPwTarget, setResetPwTarget] = useState<ResetPasswordTarget | null>(null);

    const resetNewUser = () => { setNewUser({ first_name: '', last_name: '', login_id: '', password: '' }); newUserVh.reset(); };

    const reloadUsers = useCallback(async () => {
        if (!agency) return;
        setLoading(true);
        try {
            const [m, c] = await Promise.all([
                agencyMemberService.listForAgency(agency.id),
                agencyMemberService.linkCandidates(agency.id).catch(() => [] as AgencyLinkCandidate[]),
            ]);
            setMembers(m);
            setCandidates(c || []);
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load data.') });
        } finally {
            setLoading(false);
        }
    }, [agency]);

    // On open: seed all state from agency prop
    useEffect(() => {
        if (isOpen && agency) {
            setActiveTab(initialTab);
            setIsEditing(false);
            setAgencyName(agency.audit_agency_name ?? '');
            setDescription(agency.description ?? '');
            setIsGlobal(agency.is_global ?? 0);
            setPlanWindow({
                periodicity: (agency.plan_periodicity as PlanPeriodicity) ?? 'QUARTERLY',
                yearType: (agency.plan_year_type as PlanYearType) ?? 'FINANCIAL',
                startId: agency.plan_start_period_id ?? null,
                endId: agency.plan_end_period_id ?? null,
            });
            setUniverses(null);
            auditUniverseService.byAgency(agency.id).then(setUniverses).catch(() => setUniverses([]));
            setHeadUserId(agency.head_user_id ?? '');
            setLinkedPrograms(allPrograms.filter(p => p.agency_id === agency.id));
            editVh.reset();
            reloadUsers();
            setShowLinkUserModal(false);
            setShowLinkProgramModal(false);
            setLinkUserId('');
            setLinkProgramId('');
        }
    }, [isOpen, agency]);

    // Load head-user dropdown once on open
    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        setDropdownLoading(true);
        userService.getUsersDropdown()
            .then(rows => { if (!cancelled) setDropdownUsers(rows); })
            .catch(() => { if (!cancelled) setDropdownUsers([]); })
            .finally(() => { if (!cancelled) setDropdownLoading(false); });
        return () => { cancelled = true; };
    }, [isOpen]);

    // ── Basic Details handlers ───────────────────────────────────────────────
    const handleUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!agency) return;
        if (!canModify) { setToast({ type: 'error', message: NOT_AUTHORISED_MSG }); return; }
        // Format validation (engine 109 name / 110 description) — submit gate.
        const formatOk = editVh.validateAll([
            { name: 'audit_agency_name', code: 109, value: agencyName, required: true },
            { name: 'description', code: 110, value: description },
        ]);
        if (!formatOk) return;
        if (planWindow.startId == null) {
            setToast({ type: 'error', message: 'Choose the start period of the agency’s audit period.' });
            return;
        }
        setSubmitting(true);
        try {
            const payload: AgencyUpdatePayload = {
                audit_agency_name: agencyName.trim(),
                description: description || undefined,
                is_global: isGlobal,
                plan_periodicity: planWindow.periodicity,
            plan_year_type: planWindow.yearType,
            plan_start_period_id: planWindow.startId,
            plan_end_period_id: planWindow.endId,
            ...(planWindow.endId == null ? { clear_plan_end_period: true } : {}),
            };
            if (headUserId !== '' && headUserId !== agency.head_user_id) {
                payload.head_user_id = Number(headUserId);
            }
            await agencyService.update(agency.id, payload);
            setIsEditing(false);
            onChanged?.();
            setToast({ type: 'success', message: 'Agency updated successfully.' });
        } catch (e: any) {
            const msg = extractApiError(e, 'Failed to update agency.');
            // Group-scoped uniqueness (910) is enforced server-side; surface its
            // 409 inline on the name field instead of a toast.
            if (/exist|duplicate|unique/i.test(msg)) {
                editVh.setError('audit_agency_name', msg);
            } else {
                setToast({ type: 'error', message: msg });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggle = async () => {
        if (!agency) return;
        const isActive = (agency.is_active ?? 1) === 1;
        const confirmFn = isActive ? confirmWarning : confirmActive;
        const ok = await confirmFn({
            title: isActive ? 'Inactivate Agency?' : 'Reactivate Agency?',
            text: isActive
                ? 'The record will be retained but marked Inactive.'
                : 'The agency will become active again.',
            confirmText: isActive ? 'Yes, Inactivate' : 'Yes, Reactivate',
        });
        if (!ok) return;
        setToggling(true);
        try {
            if (isActive) {
                await agencyService.inactivate(agency.id);
            } else {
                await agencyService.reactivate(agency.id);
            }
            onChanged?.();
            setToast({ type: isActive ? 'warning' : 'success', message: isActive ? 'Inactivated' : 'Reactivated' });
            onClose();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to update status.') });
        } finally {
            setToggling(false);
        }
    };

    // ── User handlers ────────────────────────────────────────────────────────
    const handleCreateUser = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!agency) return;
        const fn = newUser.first_name.trim();
        const ln = newUser.last_name.trim();
        const lid = newUser.login_id.trim();
        const pw = newUser.password;
        if (!fn || !ln || !lid || !pw) return;
        // Format validation (engine 109) on the name fields — submit gate.
        // login_id keeps its existing trim/required shape; password is never
        // content-validated.
        const formatOk = newUserVh.validateAll([
            { name: 'first_name', code: 109, value: newUser.first_name, required: true },
            { name: 'last_name', code: 109, value: newUser.last_name, required: true },
        ]);
        if (!formatOk) return;
        setCreatingUser(true);
        try {
            const created = await userService.createUser({
                first_name: fn, last_name: ln, login_id: lid,
                email: `${lid}@anygrc.local`, password: pw,
            });
            if (!created?.id) throw new Error('User creation did not return an id.');
            await agencyMemberService.create({
                agency_id: agency.id, user_id: created.id,
                member_role: 'Staff', is_active: 1, is_approved: 0,
            });
            setShowCreateUserModal(false);
            resetNewUser();
            await reloadUsers();
            onChanged?.();
            setToast({ type: 'success', message: 'User created and linked to agency.' });
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to create user.') });
        } finally {
            setCreatingUser(false);
        }
    };

    const canSubmitNewUser = !!(
        newUser.first_name.trim() && newUser.last_name.trim() &&
        newUser.login_id.trim() && newUser.password
    );

    const handleLinkUser = async () => {
        if (!agency || !linkUserId) return;
        try {
            const payload: AgencyMemberCreatePayload = {
                agency_id: agency.id, user_id: Number(linkUserId),
                member_role: 'Staff', is_active: 1, is_approved: 0,
            };
            await agencyMemberService.create(payload);
            setShowLinkUserModal(false);
            setLinkUserId('');
            await reloadUsers();
            onChanged?.();
            setToast({ type: 'success', message: 'User linked to agency successfully.' });
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to link user.') });
        }
    };

    const handleUnlinkUser = async (memberId: number) => {
        const ok = await confirmDelete({ title: 'Remove User?', text: 'This user will be unlinked from the agency.', confirmText: 'Yes, Remove' });
        if (!ok) return;
        try {
            await agencyMemberService.remove(memberId);
            await reloadUsers();
            onChanged?.();
            setToast({ type: 'delete', message: 'User removed from agency.' });
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to remove user.') });
        }
    };

    // ── Program handlers ─────────────────────────────────────────────────────
    const linkedIds = new Set(linkedPrograms.map(p => p.id));
    // Only approved + active program templates are linkable — draft/submitted/rejected
    // templates haven't cleared maker-checker and must not appear in the picker.
    const availablePrograms = allPrograms.filter(
        p => !linkedIds.has(p.id) && !p.agency_id && (p.is_active ?? 1) === 1 && p.workflow_state === 'approved',
    );

    const handleLinkProgram = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!agency || !linkProgramId) return;
        const program = allPrograms.find(p => p.id === Number(linkProgramId));
        if (!program) return;
        try {
            const updated = await programTemplateService.setAgency(Number(linkProgramId), agency.id);
            setLinkedPrograms(prev => [...prev, { ...program, agency_id: updated.agency_id }]);
            setShowLinkProgramModal(false);
            setLinkProgramId('');
            onChanged?.();
            setToast({ type: 'success', message: 'Program linked to agency successfully.' });
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to link program.') });
        }
    };

    const handleUnlinkProgram = async (programId: number) => {
        const ok = await confirmDelete({ title: 'Unlink Program?', text: 'This program will be unlinked from the agency.', confirmText: 'Yes, Unlink' });
        if (!ok) return;
        try {
            await programTemplateService.unlinkAgency(programId);
            setLinkedPrograms(prev => prev.filter(p => p.id !== programId));
            onChanged?.();
            setToast({ type: 'delete', message: 'Program unlinked from agency.' });
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to unlink program.') });
        }
    };

    const memberLinkedUserIds = new Set(members.map(m => m.user_id).filter(Boolean) as number[]);
    // Backend already kind-filters and excludes members; the local filter is a
    // belt-and-braces guard against a race with a just-linked user.
    const availableUsers = candidates.filter(c => !memberLinkedUserIds.has(c.user_id));

    // Head shown at the top of the Team tab.
    // Only an ACTIVE member row may be the head — a revoked head-row (is_active=0)
    // must never win the banner. Prefer the row matching the agency's head_user_id.
    const activeMembers = members.filter(m => (m.is_active ?? 1) === 1);
    const headMember = activeMembers.find(m => m.user_id === agency?.head_user_id)
        || activeMembers.find(m => m.member_role === 'Agency Head') || null;
    const headDisplayName = headMember
        ? (`${headMember.first_name || ''} ${headMember.last_name || ''}`.trim() || headMember.login_id || `User #${headMember.user_id}`)
        : (dropdownUsers.find(x => x.id === Number(agency?.head_user_id))?.name ?? null);

    // Head picker (edit tab) — kind-filtered: current members + eligible
    // candidates (both already match the agency character), plus the current
    // head as a fallback so its label always renders.
    const headOptions = (() => {
        const seen = new Set<number>();
        const opts: { value: number; label: string }[] = [];
        const push = (id: number, label: string) => { if (id && !seen.has(id)) { seen.add(id); opts.push({ value: id, label }); } };
        members.forEach(m => push(m.user_id, `${m.first_name || ''} ${m.last_name || ''}`.trim() || m.login_id || `User #${m.user_id}`));
        candidates.forEach(c => push(c.user_id, c.name || c.email || `User #${c.user_id}`));
        if (headUserId) { const u = dropdownUsers.find(x => x.id === Number(headUserId)); if (u) push(u.id, `${u.name} (${u.email})`); }
        return opts;
    })();

    const isActive = (agency?.is_active ?? 1) === 1;
    const anyBusy = submitting || toggling;

    const tabs: { key: Tab; label: string }[] = [
        { key: 'edit',     label: 'Basic Details' },
        { key: 'users',    label: 'Team' },
        { key: 'universes', label: `Universes${universes ? ` (${universes.length})` : ''}` },
        { key: 'programs', label: 'Programs' },
    ];

    return (
        <>
            <Modal isOpen={isOpen && !!agency} onClose={onClose} size="lg">
                <Modal.Header>
                    {agency
                        ? isApproved && activeTab === 'edit'
                            ? <>Viewing (locked) "<span className="item-highlight">{agency.audit_agency_name}</span>"</>
                            : <>Configure "<span className="item-highlight">{agency.audit_agency_name}</span>"</>
                        : 'Configure Agency'}
                </Modal.Header>

                <Modal.Body>
                    <div className="app-modal-tabs">
                        {tabs.map(({ key, label }) => (
                            <button
                                key={key}
                                type="button"
                                data-testid={`TAB_agency_config_${key}`}
                                onClick={() => setActiveTab(key)}
                                className={`app-modal-tab${activeTab === key ? ' app-modal-tab--active' : ''}`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    {/* ── Basic Details tab ──────────────────────────────────── */}
                    {activeTab === 'edit' && (
                        <form ref={editFormRef} onSubmit={handleUpdate} data-testid="MOD_agency_config_edit" className="flex flex-col gap-y-4 pt-4">
                            {isApproved && (
                                <div className="approved-banner" data-testid="BANNER_agency_config_locked">
                                    <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
                                </div>
                            )}
                            {/* System code (AGY-N) — auto-generated on approve, read-only. */}
                            <FormControl.Input
                                label="Code"
                                value={(agency as any)?.code || '—'}
                                disabled
                                data-testid="INP_agency_config_code"
                            />
                            <ValidatedInput
                                vh={editVh}
                                name="audit_agency_name"
                                code={109}
                                required
                                label="Agency Name"
                                data-testid="INP_agency_config_name"
                                value={agencyName}
                                onChange={e => setAgencyName(e.target.value)}
                                placeholder="e.g. Deloitte & Touche"
                                disabled={!isEditing}
                            />
                            <ValidatedTextarea
                                vh={editVh}
                                name="description"
                                code={110}
                                label="Description"
                                data-testid="INP_agency_config_description"
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                placeholder="Optional — brief description of this agency"
                                rows={3}
                                disabled={!isEditing}
                            />
                            <FormControl.SearchSelect
                                label="Head (Point of Contact)"
                                data-testid="SEL_agency_config_head"
                                options={headOptions}
                                value={headUserId || null}
                                onChange={v => setHeadUserId(v ? Number(v) : '')}
                                placeholder={dropdownLoading ? 'Loading…' : '— Select user —'}
                                clearable
                                disabled={!isEditing}
                            />
                            <div className="flex items-center gap-x-2">
                                <span className="text-sm font-medium text-gray-700">Agency Type:</span>
                                <span
                                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                        agency?.agency_kind === 'Internal'
                                            ? 'bg-emerald-50 text-emerald-700'
                                            : 'bg-indigo-50 text-indigo-700'
                                    }`}
                                    data-testid="BADGE_agency_config_kind"
                                >
                                    {agency?.agency_kind === 'Internal' ? 'Internal' : 'External'}
                                </span>
                            </div>

                            <AgencyPlanWindowFields value={planWindow} onChange={setPlanWindow} disabled={!isEditing} testIdPrefix="SEL_agency_config" />

                            <FormControl.Checkbox
                                label="Is Global"
                                data-testid="CHK_agency_config_is_global"
                                checked={isGlobal === 1}
                                onChange={c => setIsGlobal(c ? 1 : 0)}
                                disabled={!isEditing}
                                noMargin
                            />
                        </form>
                    )}

                    {/* ── Users tab ──────────────────────────────────────────── */}
                    {activeTab === 'users' && (
                      <div className="mt-4 flex flex-col gap-3">
                        {/* Agency Head — sits at the top of the team. */}
                        <div className={`flex items-center gap-3 px-4 py-1 rounded-lg border ${headDisplayName ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}>
                            <Crown size={18} className={headDisplayName ? 'text-amber-500 shrink-0' : 'text-gray-300 shrink-0'} />
                            <div className="flex-1 min-w-0">
                                <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-0.5">Agency Head</div>
                                {headDisplayName
                                    ? <div className="text-sm font-medium text-gray-800 truncate" data-testid="LBL_agency_config_head_name">{headDisplayName}</div>
                                    : <div className="text-sm text-gray-400 italic">No Agency Head yet — set one in Basic Details.</div>}
                            </div>
                        </div>

                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                                <span className="text-xs uppercase font-bold text-gray-500 tracking-wider">
                                    Audit Team ({members.length})
                                </span>
                                {canEdit && (
                                    <div className="flex items-center gap-2">
                                        <Button variant="outline" icon={UserPlus} type="button" data-testid="BTN_agency_config_create_user_open" onClick={() => { if (blockedByLock()) return; setShowCreateUserModal(true); }} title={isApproved ? AGENCY_LOCKED_MSG : undefined} className={isApproved ? 'opacity-50 cursor-not-allowed' : ''}>
                                            Create User
                                        </Button>
                                        {!hideLinkUser && (
                                            <Button variant="fill" icon={Plus} type="button" data-testid="BTN_agency_config_link_user_open" onClick={() => { if (blockedByLock()) return; setLinkUserId(''); setShowLinkUserModal(true); }} title={isApproved ? AGENCY_LOCKED_MSG : undefined} className={isApproved ? 'opacity-50 cursor-not-allowed' : ''}>
                                                Link User
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
                                {loading && <div className="p-6 text-sm text-gray-400 text-center">Loading…</div>}
                                {!loading && members.length === 0 && (
                                    <div className="p-8 text-center text-sm text-gray-400 italic">No team members yet.</div>
                                )}
                                {!loading && members.map(m => {
                                    const name =
                                        `${m.first_name || ''} ${m.last_name || ''}`.trim()
                                        || m.login_id
                                        || `User #${m.user_id}`;
                                    const displayLoginId = m.login_id;
                                    return (
                                        <div key={m.id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                                            <div>
                                                <div className="text-sm font-medium text-gray-800">{name}</div>
                                                <div className="text-xs text-gray-400 mt-0.5">
                                                    {m.member_role || 'Staff'}
                                                    {displayLoginId && (
                                                        <span style={{ marginLeft: 8, color: 'var(--text-muted, #9ca3af)' }}>
                                                            · {displayLoginId}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                {canEdit && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            data-testid={`BTN_agency_config_user_row_${m.id}_edit`}
                                                            title={isApproved ? AGENCY_LOCKED_MSG : 'Edit user'}
                                                            className={`p-1.5 text-gray-400 rounded ${isApproved ? 'opacity-50 cursor-not-allowed' : 'hover:text-indigo-600 hover:bg-indigo-50'}`}
                                                            onClick={() => { if (blockedByLock()) return; setEditUserTarget({
                                                                user_id: m.user_id,
                                                                login_id: m.login_id ?? undefined,
                                                                first_name: m.first_name || undefined,
                                                                last_name: m.last_name || undefined,
                                                            }); }}
                                                        >
                                                            <Edit2 size={14} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            data-testid={`BTN_agency_config_user_row_${m.id}_reset_pw`}
                                                            title={isApproved ? AGENCY_LOCKED_MSG : 'Reset password'}
                                                            className={`p-1.5 text-gray-400 rounded ${isApproved ? 'opacity-50 cursor-not-allowed' : 'hover:text-amber-600 hover:bg-amber-50'}`}
                                                            onClick={() => { if (blockedByLock()) return; setResetPwTarget({
                                                                user_id: m.user_id,
                                                                display_name: name,
                                                            }); }}
                                                        >
                                                            <Key size={14} />
                                                        </button>
                                                        <button type="button" data-testid={`BTN_agency_config_user_row_${m.id}_remove`} onClick={() => { if (blockedByLock()) return; handleUnlinkUser(m.id); }}
                                                            className={`p-1.5 text-gray-400 rounded ${isApproved ? 'opacity-50 cursor-not-allowed' : 'hover:text-red-600 hover:bg-red-50'}`} title={isApproved ? AGENCY_LOCKED_MSG : 'Remove from agency'}>
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                      </div>
                    )}

                    {/* ── Universes tab (AUA-10, read-only here) ─────────────── */}
                    {activeTab === 'universes' && (
                        <div className="border border-gray-200 rounded-lg overflow-hidden mt-4" data-testid="PNL_agency_config_universes">
                            <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                                <span className="text-xs uppercase font-bold text-gray-500 tracking-wider">
                                    Universes held · {timelineLabel(agency?.plan_periodicity)} timeline · from {agency?.plan_start_period_label ?? '—'}{agency?.plan_end_period_label ? ` to ${agency.plan_end_period_label}` : ' (open-ended)'}
                                </span>
                                <span className="text-xs text-gray-500">Assign from Audits → Universe</span>
                            </div>
                            {universes === null ? (
                                <div className="px-4 py-6 text-sm text-gray-400">Loading…</div>
                            ) : universes.length === 0 ? (
                                <div className="px-4 py-6 text-sm text-gray-400 italic">No universe has been assigned to this agency yet.</div>
                            ) : (
                                <table className="table-list-table">
                                    <thead>
                                        <tr>
                                            <th className="table-list-th">Year</th>
                                            <th className="table-list-th">Code</th>
                                            <th className="table-list-th">Universe</th>
                                            <th className="table-list-th">Category</th>
                                            <th className="table-list-th table-list-cell--right">Units</th>
                                            <th className="table-list-th">Plan due</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {universes.map(u => (
                                            <tr key={`${u.fiscal_year}-${u.universe_id}`} className="table-list-row" data-testid={`ROW_agency_config_universe_${u.universe_id}_${u.fiscal_year}`}>
                                                <td className="table-list-cell">{u.fiscal_year_label}</td>
                                                <td className="table-list-cell"><span className="cell-code">{u.universe_code}</span></td>
                                                <td className="table-list-cell"><span className="table-list-cell-title">{u.universe_name}</span></td>
                                                <td className="table-list-cell">{u.category}</td>
                                                <td className="table-list-cell table-list-cell--right">{u.unit_count}</td>
                                                <td className="table-list-cell">{u.plan_due_date ? new Date(`${u.plan_due_date}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}

                    {/* ── Programs tab ───────────────────────────────────────── */}
                    {activeTab === 'programs' && (
                        <div className="border border-gray-200 rounded-lg overflow-hidden mt-4">
                            <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                                <span className="text-xs uppercase font-bold text-gray-500 tracking-wider">
                                    Linked Programs ({linkedPrograms.length})
                                </span>
                                {canEdit && (
                                    <Button variant="fill" icon={Link} type="button" data-testid="BTN_agency_config_link_program_open" onClick={() => { if (blockedByLock()) return; setLinkProgramId(''); setShowLinkProgramModal(true); }} title={isApproved ? AGENCY_LOCKED_MSG : undefined} className={isApproved ? 'opacity-50 cursor-not-allowed' : ''}>
                                        Link Program
                                    </Button>
                                )}
                            </div>
                            <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
                                {linkedPrograms.length === 0 && (
                                    <div className="p-8 text-center text-sm text-gray-400 italic">No programs linked yet.</div>
                                )}
                                {linkedPrograms.map(p => (
                                    <div key={p.id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                                        <div>
                                            <div className="text-sm font-medium text-gray-800">{p.audit_program_name}</div>
                                            <div className="text-xs text-gray-500 mt-0.5">
                                                {p.audit_program_code}
                                                {p.cadence && <span className="ml-2">· {p.cadence}</span>}
                                                {p.ownership_scope && (
                                                    <span className="ml-2 px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{p.ownership_scope}</span>
                                                )}
                                            </div>
                                        </div>
                                        {canEdit && (
                                            <button type="button" data-testid={`BTN_agency_config_program_row_${p.id}_unlink`} onClick={() => { if (blockedByLock()) return; handleUnlinkProgram(p.id); }}
                                                className={`p-1.5 text-gray-400 rounded ${isApproved ? 'opacity-50 cursor-not-allowed' : 'hover:text-red-600 hover:bg-red-50'}`} title={isApproved ? AGENCY_LOCKED_MSG : 'Unlink program'}>
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer className={activeTab === 'edit' && !isApproved ? 'app-modal-footer--split' : ''}>
                    {activeTab === 'edit' && !isApproved && (
                        <>
                            <Button
                                variant={isActive ? 'inactive-outline' : 'active-outline'}
                                icon={isActive ? CircleMinus : CircleCheck}
                                type="button"
                                data-testid="BTN_agency_config_toggle_active"
                                onClick={handleToggle}
                                disabled={anyBusy}
                            >
                                {toggling ? '...' : isActive ? 'Inactive' : 'Active'}
                            </Button>
                            <div className="flex gap-2">
                                {isEditing ? (
                                    <Button
                                        type="button"
                                        variant="update"
                                        icon={Check}
                                        data-testid="BTN_agency_config_update"
                                        disabled={permLoading || !canModify || anyBusy}
                                        title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                                        onClick={() => editFormRef.current?.requestSubmit()}
                                    >
                                        {submitting ? 'Updating...' : 'Update'}
                                    </Button>
                                ) : (
                                    <Button variant="fill" icon={Edit2} type="button" data-testid="BTN_agency_config_edit" onClick={() => setIsEditing(true)} disabled={permLoading || !canModify || anyBusy} title={!canModify ? NOT_AUTHORISED_MSG : undefined}>
                                        Edit
                                    </Button>
                                )}
                            </div>
                        </>
                    )}
                </Modal.Footer>
            </Modal>

            {/* ── Link User sub-modal ──────────────────────────────────────── */}
            <Modal isOpen={showLinkUserModal} onClose={() => setShowLinkUserModal(false)} size="sm">
                <Modal.Header>Link User to Agency</Modal.Header>
                <Modal.Body>
                    <FormControl.SearchSelect
                        label="User"
                        required
                        data-testid="SEL_agency_config_link_user"
                        options={availableUsers.map(c => ({
                            value: c.user_id,
                            label: c.name || `User #${c.user_id}`,
                        }))}
                        value={linkUserId || null}
                        onChange={v => setLinkUserId(v ? Number(v) : '')}
                        placeholder="— Select user —"
                        helper={availableUsers.length === 0
                            ? (agency?.agency_kind === 'Internal'
                                ? 'No eligible internal staff to link.'
                                : 'No eligible External Auditors to link.')
                            : undefined}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="fill" icon={Plus} type="button" data-testid="BTN_agency_config_link_user_submit" onClick={handleLinkUser} disabled={!linkUserId}>
                        Link User
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ── Link Program sub-modal ───────────────────────────────────── */}
            <Modal isOpen={showLinkProgramModal} onClose={() => setShowLinkProgramModal(false)} size="sm">
                <Modal.Header>Link Program to Agency</Modal.Header>
                <Modal.Body>
                    <form id="link-program-form" data-testid="MOD_agency_config_link_program" onSubmit={handleLinkProgram}>
                        <FormControl.SearchSelect
                            label="Program"
                            required
                            data-testid="SEL_agency_config_link_program"
                            options={availablePrograms.map(p => ({
                                value: p.id,
                                label: `${p.audit_program_name} (${p.audit_program_code})`,
                            }))}
                            value={linkProgramId || null}
                            onChange={v => setLinkProgramId(v ? Number(v) : '')}
                            placeholder="— Select program —"
                            helper={availablePrograms.length === 0 ? 'No unassigned programs available.' : undefined}
                        />
                    </form>
                </Modal.Body>
                <Modal.Footer>
                    <Button type="submit" form="link-program-form" variant="fill" icon={Save} data-testid="BTN_agency_config_link_program_submit" disabled={!linkProgramId}>
                        Submit
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ── Create User sub-modal ────────────────────────────────────── */}
            <Modal isOpen={showCreateUserModal} onClose={() => { setShowCreateUserModal(false); resetNewUser(); }} size="sm">
                <Modal.Header>Create User</Modal.Header>
                <Modal.Body>
                    <form id="create-user-form" data-testid="MOD_agency_config_create_user" onSubmit={handleCreateUser} className="grid grid-cols-2 gap-x-4 gap-y-4">
                        <ValidatedInput vh={newUserVh} name="first_name" code={109} required label="First Name" data-testid="INP_agency_config_new_user_first_name" value={newUser.first_name} onChange={e => setNewUser({ ...newUser, first_name: e.target.value })} />
                        <ValidatedInput vh={newUserVh} name="last_name" code={109} required label="Last Name" data-testid="INP_agency_config_new_user_last_name" value={newUser.last_name} onChange={e => setNewUser({ ...newUser, last_name: e.target.value })} />
                        <FormControl.Input label="Login ID" required data-testid="INP_agency_config_new_user_login_id" placeholder="e.g. siva.prasad" value={newUser.login_id} onChange={e => setNewUser({ ...newUser, login_id: e.target.value })} />
                        <FormControl.Input label="Password" required type="password" data-testid="INP_agency_config_new_user_password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} />
                    </form>
                </Modal.Body>
                <Modal.Footer>
                    <Button type="submit" form="create-user-form" variant="fill" icon={Save} data-testid="BTN_agency_config_new_user_submit" disabled={!canSubmitNewUser || creatingUser}>
                        {creatingUser ? 'Submitting...' : 'Submit'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ── Edit User sub-modal ──────────────────────────────────────── */}
            <EditUserModal
                isOpen={!!editUserTarget}
                onClose={() => setEditUserTarget(null)}
                target={editUserTarget}
                onSaved={async () => {
                    await reloadUsers();
                    onChanged?.();
                    setToast({ type: 'success', message: 'User updated.' });
                }}
            />

            {/* ── Reset Password sub-modal ─────────────────────────────────── */}
            <ResetPasswordModal
                isOpen={!!resetPwTarget}
                onClose={() => setResetPwTarget(null)}
                target={resetPwTarget}
            />

            <Toast toast={toast} onClose={() => setToast(null)} />
        </>
    );
};

export default AgencyConfigureModal;
